import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, RotateCcw, ExternalLink } from "lucide-react";

export default function RepeatFaults() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/repeat-links").then((r) => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const cancel = async (wid, itemId, prevItemId) => {
    if (!window.confirm("Bu tekrar arıza bağlantısı iptal edilsin mi?")) return;
    try {
      const params = prevItemId ? `?prev_item_id=${encodeURIComponent(prevItemId)}` : "";
      const { data } = await api.delete(`/repeat-links/${wid}/${itemId}${params}`);
      toast.success(data.reverted ? "Bağlantı iptal edildi, puan geri yüklendi" : "Bağlantı iptal edildi");
      if (data.admin_note) toast.info(data.admin_note);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "İptal edilemedi");
    }
  };

  return (
    <div className="space-y-6" data-testid="repeat-faults-page">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-mono">Admin • Yönetim</div>
        <h1 className="mt-1 font-heading font-bold text-3xl lg:text-4xl tracking-tight text-slate-900 flex items-center gap-3">
          <AlertTriangle className="w-8 h-8 text-red-600" /> Tekrar Arıza Bağlantıları
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          Kim tarafından hangi tarihte "Evet, tekrar arıza" olarak işaretlendi. Yanlış bağlantıları iptal edebilirsiniz;
          iptalde önceki kalemin puanı (açık aydaysa) geri yüklenir.
        </p>
      </div>

      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="font-heading text-lg">Bağlantı Listesi ({rows.length})</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {loading && <div className="text-sm text-slate-500">Yükleniyor…</div>}
          {!loading && rows.length === 0 && <div className="text-sm text-slate-500">Henüz tekrar arıza bağlantısı yok.</div>}
          {!loading && rows.length > 0 && (
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="text-xs uppercase tracking-wider">Tarih</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Plaka</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Sistem / Parça / Konum</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">İşareti Koyan</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Şiddet</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-right">Aksiyon</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.flatMap((row) => {
                    const it = row.item || {};
                    const links = (row.links && row.links.length) ? row.links : [{
                      prev_work_order_id: it.repeat_of?.work_order_id,
                      prev_item_id: it.repeat_of?.item_id,
                      matched_locations: [],
                      severity: it.repeat_of?.severity || "-",
                      decision: "yes",
                    }];
                    const locs = (it.locations && it.locations.length) ? it.locations : [it.location || "Genel"];
                    return links.map((lk, li) => (
                      <TableRow key={`${row.work_order_id}-${it.item_id}-${lk.prev_item_id || li}`} className="hover:bg-slate-50" data-testid={`repeat-row-${(it.item_id || "").slice(0,8)}-${li}`}>
                        <TableCell className="font-mono text-xs">{(row.date || "").slice(0, 10)}</TableCell>
                        <TableCell className="font-mono">{row.vehicle_plate}</TableCell>
                        <TableCell className="text-xs">
                          <div className="font-semibold">{it.system_name || "-"}</div>
                          <div className="text-slate-500">{it.part_name || "—"} · {locs.join(", ")}</div>
                          {lk.matched_locations && lk.matched_locations.length > 0 && (
                            <div className="mt-1"><Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 text-[10px]">Eşleşen: {lk.matched_locations.join(", ")}</Badge></div>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          <div>{lk.decided_by || it.repeat_decided_by || "-"}</div>
                          <div className="text-slate-400 font-mono">{((lk.decided_at || it.repeat_decided_at) || "").slice(0, 16).replace("T", " ")}</div>
                        </TableCell>
                        <TableCell>
                          {lk.severity === "strong"
                            ? <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Güçlü</Badge>
                            : lk.severity === "weak"
                              ? <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Zayıf</Badge>
                              : <Badge>—</Badge>}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" onClick={() => cancel(row.work_order_id, it.item_id, lk.prev_item_id)}
                            data-testid={`repeat-cancel-${(it.item_id || "").slice(0,8)}-${li}`}>
                            <RotateCcw className="w-3.5 h-3.5 mr-1" /> İptal Et
                          </Button>
                        </TableCell>
                      </TableRow>
                    ));
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
